import type {
  StorageEstimate,
  StorageInspector,
} from '@obraplan/core/application/ports/storage-inspector';

/**
 * Consumo e durabilidade do armazenamento do navegador.
 *
 * Sem servidor, a cota é um limite real e o expurgo automático é um risco real. As fotos
 * da conferência dominam o consumo (PLANO_GERAL.md §4.5), então o usuário precisa ver
 * quanto está usando — e o sistema precisa pedir persistência.
 */
export class BrowserStorageInspector implements StorageInspector {
  async estimate(): Promise<StorageEstimate> {
    const storage = globalThis.navigator?.storage;

    if (storage?.estimate === undefined) {
      return {
        usageBytes: 0,
        quotaBytes: 0,
        usedRatio: 0,
        persisted: false,
        supported: false,
      };
    }

    const [estimate, persisted] = await Promise.all([
      storage.estimate(),
      storage.persisted === undefined ? Promise.resolve(false) : storage.persisted(),
    ]);

    const usageBytes = estimate.usage ?? 0;
    const quotaBytes = estimate.quota ?? 0;

    return {
      usageBytes,
      quotaBytes,
      usedRatio: quotaBytes > 0 ? usageBytes / quotaBytes : 0,
      persisted,
      supported: true,
    };
  }

  /**
   * Pede ao navegador que não expurgue os dados automaticamente.
   * Pode ser negado — o retorno diz o que aconteceu, e a interface avisa o usuário.
   */
  async requestPersistence(): Promise<boolean> {
    const storage = globalThis.navigator?.storage;
    if (storage?.persist === undefined) return false;
    try {
      return await storage.persist();
    } catch {
      return false;
    }
  }
}
